import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ActiveUsersTab from './components/ActiveUsersTab';
import PendingInvitationsTab from './components/PendingInvitationsTab';
import RoleManagementTab from './components/RoleManagementTab';
import InviteUserModal from './components/InviteUserModal';
import LogoUploadTab from './components/LogoUploadTab';
import ResourceImportExportTab from './components/ResourceImportExportTab';
import CustomerImportTab from './components/CustomerImportTab';
import DefaultPhasesTab from '../settings/components/DefaultPhasesTab';

const TeamAdministration = () => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('active-users');
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [realtimeError, setRealtimeError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const RealtimeErrorType = {
    CONNECTION_FAILED: 'CONNECTION_FAILED',
    SUBSCRIPTION_ERROR: 'SUBSCRIPTION_ERROR'
  };

  useEffect(() => {
    loadData();

    // Real-time subscription for user_profiles
    const usersChannel = supabase?.channel('team_users_changes')?.on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'user_profiles' },
      () => {
        loadUsers();
      }
    )?.subscribe();

    // Real-time subscription for invitations
    const invitationsChannel = supabase?.channel('team_invitations_changes')?.on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'invitations' },
      () => {
        loadInvitations();
      }
    )?.subscribe();

    // Cleanup subscriptions
    return () => {
      supabase?.removeChannel(usersChannel);
      supabase?.removeChannel(invitationsChannel);
    };
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadUsers(), loadInvitations()]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const { data: usersData, error: usersError } = await supabase
        ?.from('user_profiles')
        ?.select('id, email, full_name, first_name, last_name, nickname, role, is_active, is_pm, created_at, last_sign_in_at, must_change_password')
        ?.order('created_at', { ascending: false });

      if (usersError) {
        if (usersError?.code === 'PGRST116') {
          setUsers([]);
          return;
        }
        throw usersError;
      }

      setUsers(usersData || []);
    } catch (error) {
      console.error('Error loading users:', error?.message);
      setUsers([]);
    }
  };

  const loadInvitations = async () => {
    try {
      const { data, error } = await supabase?.from('invitations')?.select(`
          *,
          inviter:user_profiles!invitations_invited_by_fkey(full_name, email)
        `)?.order('created_at', { ascending: false });

      if (error) {
        if (error?.code === 'PGRST116') {
          setInvitations([]);
          return;
        }
        throw error;
      }

      setInvitations(data || []);
    } catch (error) {
      console.error('Error loading invitations:', error?.message);
      setInvitations([]);
    }
  };

  const handleInviteSent = () => {
    loadInvitations();
    setIsInviteModalOpen(false);
  };

  const handleUserUpdated = () => {
    loadUsers();
  };

  const handleInvitationUpdated = () => {
    loadInvitations();
  };

  const tabs = [
    { id: 'active-users', label: 'Active Users', icon: 'Users' },
    { id: 'pending-invitations', label: 'Pending Invitations', icon: 'Mail' },
    { id: 'role-management', label: 'Role Management', icon: 'Shield' },
    { id: 'default-phases', label: 'Default Phases', icon: 'GitBranch' },
    { id: 'logo-upload', label: 'Logo Upload', icon: 'Image' },
    { id: 'resource-import-export', label: 'Resource Import/Export', icon: 'ArrowLeftRight' },
    { id: 'customer-import', label: 'Customer Import', icon: 'UserPlus' }
  ];

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Real-time Error Alert */}
        {realtimeError && (
          <div className={`rounded-lg border p-4 flex items-start gap-3 ${
            realtimeError?.type === RealtimeErrorType?.CONNECTION_FAILED
              ? 'bg-red-50 border-red-200 text-red-800' :'bg-yellow-50 border-yellow-200 text-yellow-800'
          }`}>
            <Icon 
              name={isRetrying ? 'RefreshCw' : 'AlertTriangle'} 
              size={20} 
              className={isRetrying ? 'animate-spin' : ''}
            />
            <div className="flex-1">
              <p className="font-medium text-sm">
                {realtimeError?.message}
              </p>
              {realtimeError?.attemptCount && (
                <p className="text-xs mt-1 opacity-80">
                  Retry attempt {realtimeError?.attemptCount}/3
                </p>
              )}
            </div>
            {!isRetrying && realtimeError?.type === RealtimeErrorType?.CONNECTION_FAILED && (
              <button
                onClick={() => window.location?.reload()}
                className="text-sm font-medium underline hover:no-underline"
              >
                Refresh Page
              </button>
            )}
            {!isRetrying && realtimeError?.type !== RealtimeErrorType?.CONNECTION_FAILED && (
              <button
                onClick={() => setRealtimeError(null)}
                className="text-sm font-medium"
              >
                <Icon name="X" size={16} />
              </button>
            )}
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <Icon name="Settings2" size={24} className="text-accent" />
              </div>
              Site Administration
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Manage team members, default phases, send invitations, and control access permissions
            </p>
          </div>
          <Button
            variant="default"
            iconName="UserPlus"
            iconPosition="left"
            onClick={() => setIsInviteModalOpen(true)}
          >
            Invite User
          </Button>
        </div>

        {/* Summary Stats */}
        {!loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Icon name="Users" size={20} className="text-accent" />
                </div>
                <span className="text-xs md:text-sm font-medium text-muted-foreground">Total</span>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-foreground mb-1">{users?.length || 0}</p>
              <p className="text-xs md:text-sm text-muted-foreground">Team Members</p>
            </div>

            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Icon name="UserCheck" size={20} className="text-green-500" />
                </div>
                <span className="text-xs md:text-sm font-medium text-muted-foreground">Active</span>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-foreground mb-1">
                {users?.filter(u => u?.status === 'active' || !u?.status)?.length || 0}
              </p>
              <p className="text-xs md:text-sm text-muted-foreground">Active Users</p>
            </div>

            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                  <Icon name="Mail" size={20} className="text-yellow-500" />
                </div>
                <span className="text-xs md:text-sm font-medium text-muted-foreground">Pending</span>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-foreground mb-1">
                {invitations?.filter(i => i?.status === 'pending')?.length || 0}
              </p>
              <p className="text-xs md:text-sm text-muted-foreground">Pending Invitations</p>
            </div>

            <div className="bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <Icon name="Shield" size={20} className="text-purple-500" />
                </div>
                <span className="text-xs md:text-sm font-medium text-muted-foreground">Role</span>
              </div>
              <p className="text-2xl md:text-3xl font-bold text-foreground mb-1">
                {users?.filter(u => u?.role === 'admin')?.length || 0}
              </p>
              <p className="text-xs md:text-sm text-muted-foreground">Administrators</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-card border border-border rounded-xl shadow-sm">
          <div className="border-b border-border overflow-x-auto">
            <div className="flex min-w-max">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors relative ${
                    activeTab === tab?.id
                      ? 'text-accent border-b-2 border-accent' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                  {tab?.count !== undefined && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      activeTab === tab?.id
                        ? 'bg-accent text-white' :'bg-muted text-muted-foreground'
                    }`}>
                      {tab?.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {loading && activeTab !== 'default-phases' ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
                  <p className="text-sm text-muted-foreground">Loading data...</p>
                </div>
              </div>
            ) : (
              <>
                {activeTab === 'active-users' && (
                  <ActiveUsersTab
                    users={users}
                    currentUser={userProfile}
                    onUserUpdated={handleUserUpdated}
                  />
                )}
                {activeTab === 'pending-invitations' && (
                  <PendingInvitationsTab
                    invitations={invitations}
                    onInvitationUpdated={handleInvitationUpdated}
                  />
                )}
                {activeTab === 'role-management' && (
                  <RoleManagementTab users={users} onUserUpdate={handleUserUpdated} />
                )}
                {activeTab === 'default-phases' && <DefaultPhasesTab />}
                {activeTab === 'logo-upload' && <LogoUploadTab />}
                {activeTab === 'resource-import-export' && <ResourceImportExportTab />}
                {activeTab === 'customer-import' && <CustomerImportTab />}
              </>
            )}
          </div>
        </div>
      </div>
      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInviteSent={handleInviteSent}
      />
    </MainLayout>
  );
};

export default TeamAdministration;